import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useSocket } from '@/contexts/SocketContext';
import { useSipPhone } from '@/contexts/SipPhoneContext';
import { useModules } from '@/contexts/ModuleContext';
import { apiClient } from '@/lib/api';
import {
  MOBILE_ACTION_ICON,
  type MobileHeaderActionId,
} from '@/lib/mobileHeaderActions';
import { useMobileBottomNavActions } from '@/lib/mobileBottomNav';
import { useMobileShortcutAvailability } from '@/lib/useMobileShortcutAvailability';
import type { CountData, NotificationUnreadCount } from '@easyflow/shared';

interface MobileBottomNavProps {
  // Apre il drawer hamburger esistente (gestito a livello di Layout). Lo slot
  // "Altro" della bottom bar invoca questa callback: è hard-coded e non
  // configurabile dall'utente.
  onOpenMore: () => void;
}

// Altezza nominale della barra (in px). Il Layout aggiunge un padding-bottom
// equivalente al `<main>` su mobile per non far finire contenuto sotto la
// barra. Il valore reale può crescere per safe-area iOS via env().
export const MOBILE_BOTTOM_NAV_HEIGHT_PX = 64;

interface ActionDescriptor {
  id: MobileHeaderActionId | 'more';
  label: string;
  onClick: () => void;
  isActive: boolean;
  badge?: number;
  // Pallino verde lampeggiante (es. Telefono SIP in chiamata).
  pulse?: boolean;
}

export default function MobileBottomNav({ onOpenMore }: MobileBottomNavProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { tenantSlug } = useParams();
  const slug = tenantSlug || user?.tenantSlug || '';
  const { isModuleEnabled } = useModules();

  const sipPhone = useSipPhone();
  const { isRegistered, isInCall, openPhone } = sipPhone;

  const selectedIds = useMobileBottomNavActions(user?.id);
  const { filterIds, isAvailable } = useMobileShortcutAvailability();
  const visibleSelected = useMemo(
    () => filterIds(selectedIds).slice(0, 4),
    [selectedIds, filterIds],
  );

  // ── Badge contatori condivisi ──
  const [messengerUnread, setMessengerUnread] = useState(0);
  const fetchMessengerUnread = useCallback(async () => {
    try {
      const res = await apiClient.get<CountData>('/messenger/unread-count');
      if (res.success) setMessengerUnread(res.data?.count || 0);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!user || !isModuleEnabled('messenger')) return;
    fetchMessengerUnread();
    const id = setInterval(fetchMessengerUnread, 30000);
    const onMsg = () => fetchMessengerUnread();
    window.addEventListener('messenger:new-message', onMsg);
    return () => {
      clearInterval(id);
      window.removeEventListener('messenger:new-message', onMsg);
    };
  }, [user?.id, fetchMessengerUnread, isModuleEnabled]);

  const { socket, socketVersion } = useSocket();
  useEffect(() => {
    if (!socket || !user || !isModuleEnabled('messenger')) return;
    const onUnread = () => setMessengerUnread((n) => n + 1);
    socket.on('chat:unread', onUnread);
    return () => {
      socket.off('chat:unread', onUnread);
    };
  }, [socket, user?.id, isModuleEnabled]);

  // Self-healing: su ogni (ri)connessione del socket riallinea subito il badge
  // chat. Il poll a 30s resta la rete di sicurezza per i buchi prolungati.
  useEffect(() => {
    if (!user || !isModuleEnabled('messenger')) return;
    if (socketVersion === 0) return;
    fetchMessengerUnread();
  }, [socketVersion, user?.id, fetchMessengerUnread, isModuleEnabled]);

  const [notifUnread, setNotifUnread] = useState(0);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const fetchNotif = async () => {
      try {
        const res = await apiClient.get<NotificationUnreadCount>('/notifications/unread-count');
        const count = res.data?.count ?? 0;
        if (alive) setNotifUnread(Number(count) || 0);
      } catch {
        // ignore
      }
    };
    fetchNotif();
    const id = setInterval(fetchNotif, 60000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [user?.id, slug]);

  // Restiamo allineati al socket di NotificationBell (stesso evento) — il
  // backend pubblica già `notification:new` / `notification:count` per
  // questo utente, quindi il badge si aggiorna in tempo reale anche qui.
  useEffect(() => {
    if (!socket || !user) return;
    const onNew = () => setNotifUnread((n) => n + 1);
    const onCount = (data: { count?: number }) => {
      if (typeof data?.count === 'number') setNotifUnread(data.count);
    };
    const onRead = () => setNotifUnread((n) => Math.max(0, n - 1));
    socket.on('notification:new', onNew);
    socket.on('notification:count', onCount);
    socket.on('notification:read', onRead);
    return () => {
      socket.off('notification:new', onNew);
      socket.off('notification:count', onCount);
      socket.off('notification:read', onRead);
    };
  }, [socket, user?.id]);

  // ── Helpers ──
  const isPathActive = useCallback(
    (path: string) => location.pathname === path || location.pathname.startsWith(path + '/'),
    [location.pathname],
  );

  const buildAction = useCallback(
    (id: MobileHeaderActionId): ActionDescriptor | null => {
      if (!isAvailable(id)) return null;
      switch (id) {
        case 'calendar':
          return {
            id,
            label: 'Calendario',
            isActive: isPathActive(`/${slug}/calendar`),
            onClick: () => navigate(`/${slug}/calendar`),
          };
        case 'messaging-chat':
          return {
            id,
            label: 'Chat',
            isActive: isPathActive(`/${slug}/messenger`),
            onClick: () => navigate(`/${slug}/messenger`),
            badge: messengerUnread,
          };
        case 'messaging-email':
          return {
            id,
            label: 'Email',
            isActive: isPathActive(`/${slug}/messaging/email`),
            onClick: () => navigate(`/${slug}/messaging/email`),
          };
        case 'quick-ticket':
          return {
            id,
            label: 'Ticket',
            isActive: false,
            onClick: () => window.dispatchEvent(new CustomEvent('open-quick-ticket')),
          };
        case 'theme-toggle': {
          const toggle = () => {
            const cur = (localStorage.getItem('theme') as 'light' | 'dark' | null) ||
              (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light');
            const next = cur === 'light' ? 'dark' : 'light';
            localStorage.setItem('theme', next);
            document.documentElement.classList.toggle('dark', next === 'dark');
          };
          return { id, label: 'Tema', isActive: false, onClick: toggle };
        }
        case 'ai-assistant':
          return {
            id,
            label: 'Assistente',
            isActive: false,
            onClick: () => window.dispatchEvent(new CustomEvent('open-personal-assistant')),
          };
        case 'token-usage':
          return {
            id,
            label: 'Crediti',
            isActive: false,
            onClick: () => navigate(`/${slug}/dashboard?tab=tokens`),
          };
        case 'dashboard':
          return {
            id,
            label: 'Dashboard',
            isActive: location.pathname === `/${slug}/dashboard`,
            onClick: () => navigate(`/${slug}/dashboard`),
          };
        case 'sip-phone':
          return {
            id,
            label: 'Telefono',
            isActive: false,
            onClick: () => openPhone(),
            pulse: isInCall || isRegistered,
          };
        case 'notifications':
          return {
            id,
            label: 'Notifiche',
            isActive: false,
            onClick: () => window.dispatchEvent(new CustomEvent('open-notifications-panel')),
            badge: notifUnread,
          };
        case 'wiki':
          return {
            id,
            label: 'Wiki',
            isActive: isPathActive(`/${slug}/company-wiki`),
            onClick: () => navigate(`/${slug}/company-wiki`),
          };
        case 'feed':
          return {
            id,
            label: 'Feed',
            isActive: isPathActive(`/${slug}/messenger/feed`),
            onClick: () => navigate(`/${slug}/messenger/feed`),
          };
        case 'messaging-feed':
          return {
            id,
            label: 'Feed',
            isActive: isPathActive(`/${slug}/messenger/feed`),
            onClick: () => navigate(`/${slug}/messenger/feed`),
          };
        case 'messaging-groups':
          return {
            id,
            label: 'Gruppi',
            isActive: isPathActive(`/${slug}/messenger/gruppi`) || isPathActive(`/${slug}/messenger/workgroups`),
            onClick: () => navigate(`/${slug}/messenger/gruppi`),
          };
        case 'messaging-channels':
          return {
            id,
            label: 'Canali',
            isActive: isPathActive(`/${slug}/messenger/canali`),
            onClick: () => navigate(`/${slug}/messenger/canali`),
          };
        case 'ai-tools':
          return {
            id,
            label: 'AI Tools',
            isActive: isPathActive(`/${slug}/ai-tools`),
            onClick: () => navigate(`/${slug}/ai-tools`),
          };
        default:
          return null;
      }
    },
    [
      slug,
      navigate,
      isAvailable,
      isPathActive,
      messengerUnread,
      notifUnread,
      isInCall,
      isRegistered,
      openPhone,
      location.pathname,
    ],
  );

  const actions = useMemo(() => {
    const list: ActionDescriptor[] = [];
    for (const id of visibleSelected) {
      const a = buildAction(id);
      if (a) list.push(a);
    }
    // Slot fisso "Altro": apre il drawer hamburger esistente.
    list.push({
      id: 'more',
      label: 'Altro',
      isActive: false,
      onClick: onOpenMore,
    });
    return list;
  }, [visibleSelected, buildAction, onOpenMore]);

  if (!user) return null;

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-40 md:hidden',
        'bg-white/95 dark:bg-slate-900/95 backdrop-blur-md',
        'border-t border-gray-200 dark:border-gray-800 shadow-[0_-2px_12px_rgba(0,0,0,0.06)]',
      )}
      style={{
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
      aria-label="Barra di navigazione mobile"
    >
      <ul className="grid" style={{ gridTemplateColumns: `repeat(${actions.length}, minmax(0, 1fr))` }}>
        {actions.map((a) => {
          const Icon = a.id === 'more' ? Menu : MOBILE_ACTION_ICON[a.id];
          const badgeValue = typeof a.badge === 'number' ? a.badge : 0;
          const showBadge = badgeValue > 0;
          return (
            <li key={a.id} className="contents">
              <button
                type="button"
                onClick={a.onClick}
                className={cn(
                  'relative flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px]',
                  'transition-colors active:bg-gray-100 dark:active:bg-slate-800',
                  a.isActive
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-500 dark:text-gray-400',
                )}
                aria-label={a.label}
                aria-current={a.isActive ? 'page' : undefined}
              >
                <span className="relative inline-flex">
                  <Icon
                    className={cn(
                      'h-5 w-5',
                      a.isActive
                        ? 'fill-blue-600/10 stroke-[2.25]'
                        : 'fill-transparent',
                    )}
                  />
                  {a.pulse && (
                    <span
                      aria-hidden
                      className="absolute -top-0.5 -right-1 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900 animate-pulse"
                    />
                  )}
                  {showBadge && (
                    <span
                      aria-hidden
                      className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1 ring-2 ring-white dark:ring-slate-900"
                    >
                      {badgeValue > 99 ? '99+' : badgeValue}
                    </span>
                  )}
                </span>
                <span
                  className={cn(
                    'text-[10px] leading-tight font-medium truncate max-w-[64px]',
                    a.isActive && 'font-semibold',
                  )}
                >
                  {a.label}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
